// provider.js
import { saveAs } from 'file-saver';
import { store, AuthClear, DelegateSessionClear } from './store'; // Import the Redux store
import {
    AgentHasPermissionCustomerAdmin,
    AgentHasPermissionCustomerManager,
    AgentHasPermissionProjectAdmin,
} from './etc';
import { getApiUrl } from './config/hostname';
import { writeResourceCacheTo, getResourceStorage } from './utils/resourceCacheStore';

/**
 * Tracks whether an auth-failure (401) redirect is already in progress, so that
 * multiple concurrent 401 responses do not trigger several redirects. Reset to
 * false on any successful response (see fetchData/PostFormData/FetchBinary),
 * because the app uses HashRouter + navigate() with no full page reload, so a
 * never-reset module flag would silence the next 401 after a re-login.
 */
let isHandlingAuthFailure = false;

/**
 * Handle an authentication failure (HTTP 401): clear all auth state (including
 * any active delegate impersonation session, whose token takes precedence in
 * getAuthTokenFromStore) and redirect to the login page. Deduped so concurrent
 * 401s only redirect once.
 *
 * Dispatch order is significant (VOIP-1316 design D7): AuthClear() MUST run
 * before DelegateSessionClear(). Both authSlice.clear and
 * delegateSessionSlice.clear (src/store.js) call clearAllResourceCaches(),
 * which checks isDelegateTab() (reads sessionStorage['delegate_session']) to
 * decide whether to also sweep localStorage. If DelegateSessionClear() ran
 * first, the delegate_session key would already be gone by the time
 * AuthClear()'s cleanup runs, making it misidentify a delegate-tab 401 as a
 * host tab and wipe the shared localStorage that an unrelated host tab (in
 * another window) is actively using. Do not reorder this, and do not add a
 * separate explicit clearAllResourceCaches() call here — the reducer-level
 * calls (store.js) are sufficient once this order and the reducer-internal
 * order in delegateSessionSlice.clear are both correct.
 */
const handleAuthFailure = () => {
    if (isHandlingAuthFailure) return;
    isHandlingAuthFailure = true;
    try {
        store.dispatch(AuthClear());
        // Clear delegate impersonation second. Harmless no-op when no
        // delegate session is active.
        store.dispatch(DelegateSessionClear());
        localStorage.removeItem('customer_info'); // mirror onLogout cleanup
    } catch (e) {
        console.error('handleAuthFailure: cleanup error', e);
    }
    window.location.hash = '#/login';
};

/**
 * Get the current authentication token from Redux store.
 */
const getAuthTokenFromStore = () => {
    const state = store.getState();
    return state.delegateSession?.token || state.auth.token;
};

/**
 * Get the current agent information from Redux store.
 */
const getAgentInfoFromStore = () => {
    const state = store.getState();
    return state.delegateSession?.isActive
        ? state.delegateSession.agentInfo
        : state.auth.agentInfo;
};

/**
 * Core fetch wrapper for all API requests.
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} target - API endpoint (e.g., 'me', 'calls')
 * @param {object} data - Body data for the request
 * @param {boolean} requiresAuth - Whether to include the Bearer token (default: true)
 */
const fetchData = async (method, target, data = null, requiresAuth = true) => {
    try {
        const authToken = getAuthTokenFromStore();
        
        if (requiresAuth && !authToken) {
            console.error(`fetchData: Authentication token missing for request to ${target}`);
            throw new Error("Authentication token not found. Please log in.");
        }

        const endpoint = target.startsWith('/') ? target.substring(1) : target;
        
        /**
         * Logic to handle versioning:
         * If the target is 'auth/login', use it as is.
         * For other resources, automatically prepend 'v1.0/'
         */
        const isAuthRequest = endpoint.startsWith('auth/');
        const versionedPath = isAuthRequest ? endpoint : `v1.0/${endpoint}`;
        
        const requestURI = `${getApiUrl()}${versionedPath}`;

        const headers = {
            'Content-Type': 'application/json',
        };

        if (requiresAuth && authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
        }

        const options = {
            method,
            headers,
            body: data ? JSON.stringify(data) : null,
        };

        console.log(`fetchData: [${method}] requesting ${requestURI}`);
        const response = await fetch(requestURI, options);
        // Handle error responses
        if (!response.ok) {
            const errorBody = await response.text();
            console.error(`fetchData: Failed - Status: ${response.status}, URI: ${requestURI}, Body: ${errorBody}`);

            // Handle expired/invalid auth (401). Skip auth/* requests (e.g.
            // auth/login returns 401 on wrong credentials and must be handled
            // by the login form, not a global redirect).
            if (response.status === 401 && !isAuthRequest) {
                console.warn(`fetchData: 401 Unauthorized for ${requestURI} — redirecting to login`);
                handleAuthFailure();
                throw new Error('Unauthorized');
            }

            // Detect frozen account (403 DELETION_SCHEDULED)
            if (response.status === 403) {
                let errorJson = null;
                try { errorJson = JSON.parse(errorBody); } catch (_) { /* not JSON */ }

                if (errorJson && errorJson.error === 'DELETION_SCHEDULED') {
                    localStorage.setItem('deletion_info', JSON.stringify({
                        deletion_scheduled_at: errorJson.deletion_scheduled_at,
                        deletion_effective_at: errorJson.deletion_effective_at,
                    }));
                    window.location.hash = '#/account-frozen';
                    throw new Error('DELETION_SCHEDULED');
                }
            }

            const error = new Error(`Request failed (${response.status}): ${response.statusText}. Details: ${errorBody}`);
            error.status = response.status;
            throw error;
        }

        // A successful response means auth is healthy; clear any prior
        // auth-failure latch so a future 401 can redirect again (covers the
        // 204 path below too).
        isHandlingAuthFailure = false;

        // Return null for "No Content" success
        if (response.status === 204) {
            return null; 
        }

        return response.json();
    } catch (e) {
        console.error("fetchData: Unexpected error occurred.", e);
        throw e;
    }
};

/**
 * Exported API methods
 */
export const Get = (target) => fetchData('GET', target);
export const Post = (target, data, auth = true) => fetchData('POST', target, data, auth);
export const Put = (target, data) => fetchData('PUT', target, data);
export const Delete = (target) => fetchData('DELETE', target);

/**
 * Upload a file using multipart/form-data.
 * @param {string} target - API endpoint (e.g., 'files')
 * @param {FormData} formData - FormData object containing the file and metadata
 */
export const PostFormData = async (target, formData) => {
    try {
        const authToken = getAuthTokenFromStore();

        if (!authToken) {
            console.error(`PostFormData: Authentication token missing for request to ${target}`);
            throw new Error("Authentication token not found. Please log in.");
        }

        const endpoint = target.startsWith('/') ? target.substring(1) : target;
        const versionedPath = `v1.0/${endpoint}`;
        const requestURI = `${getApiUrl()}${versionedPath}`;

        const headers = {
            'Authorization': `Bearer ${authToken}`,
            // Do NOT set Content-Type — browser sets it with boundary for multipart
        };

        const options = {
            method: 'POST',
            headers,
            body: formData,
        };

        console.log(`PostFormData: [POST] requesting ${requestURI}`);
        const response = await fetch(requestURI, options);

        if (!response.ok) {
            const errorBody = await response.text();
            console.error(`PostFormData: Failed - Status: ${response.status}, URI: ${requestURI}, Body: ${errorBody}`);

            // Handle expired/invalid auth (401). This helper never serves
            // auth/* endpoints, so an unconditional check is safe.
            if (response.status === 401) {
                handleAuthFailure();
                throw new Error('Unauthorized');
            }

            // Detect frozen account (403 DELETION_SCHEDULED)
            if (response.status === 403) {
                let errorJson = null;
                try { errorJson = JSON.parse(errorBody); } catch (_) { /* not JSON */ }

                if (errorJson && errorJson.error === 'DELETION_SCHEDULED') {
                    localStorage.setItem('deletion_info', JSON.stringify({
                        deletion_scheduled_at: errorJson.deletion_scheduled_at,
                        deletion_effective_at: errorJson.deletion_effective_at,
                    }));
                    window.location.hash = '#/account-frozen';
                    throw new Error('DELETION_SCHEDULED');
                }
            }

            throw new Error(`Request failed (${response.status}): ${response.statusText}. Details: ${errorBody}`);
        }

        // Successful response: clear the auth-failure latch (covers 204 below).
        isHandlingAuthFailure = false;

        if (response.status === 204) {
            return null;
        }

        return response.json();
    } catch (e) {
        console.error("PostFormData: Unexpected error occurred.", e);
        throw e;
    }
};

/**
 * Handle file downloads with token authentication via query parameter.
 */
export const Download = (target, filename) => {
    console.log(`Download: Initializing for ${filename} at ${target}`);
    const authToken = getAuthTokenFromStore();

    if (!authToken) {
        console.error("Download: Authentication token missing.");
        alert("Authentication required. Please log in to download files.");
        return;
    }

    const endpoint = target.startsWith('/') ? target.substring(1) : target;

    /**
     * Logic to handle versioning (same as fetchData):
     * If the target is 'auth/*', use it as is.
     * For other resources, automatically prepend 'v1.0/'
     */
    const isAuthRequest = endpoint.startsWith('auth/');
    const versionedPath = isAuthRequest ? endpoint : `v1.0/${endpoint}`;

    const requestURI = `${getApiUrl()}${versionedPath}?token=${authToken}`;

    try {
        saveAs(requestURI, filename);
    } catch (e) {
        console.error("Download: Failed to initiate file save.", e);
        alert(`Failed to download ${filename}: ${e.message}`);
    }
};

/**
 * Fetch binary data from the API and return it as an ArrayBuffer.
 * Unlike Download(), this does not trigger a browser save dialog.
 * Useful for in-memory processing of binary responses (e.g., PCAP files).
 * @param {string} target - API endpoint (e.g., 'calls/{id}/pcap')
 * @returns {Promise<ArrayBuffer>} The response body as an ArrayBuffer
 */
export const FetchBinary = async (target) => {
    try {
        const authToken = getAuthTokenFromStore();

        if (!authToken) {
            throw new Error("Authentication token not found. Please log in.");
        }

        const endpoint = target.startsWith('/') ? target.substring(1) : target;

        /**
         * Logic to handle versioning (same as fetchData):
         * If the target is 'auth/*', use it as is.
         * For other resources, automatically prepend 'v1.0/'
         */
        const isAuthRequest = endpoint.startsWith('auth/');
        const versionedPath = isAuthRequest ? endpoint : `v1.0/${endpoint}`;

        const requestURI = `${getApiUrl()}${versionedPath}`;

        console.log(`FetchBinary: requesting ${requestURI}`);
        const response = await fetch(requestURI, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error(`FetchBinary: Failed - Status: ${response.status}, URI: ${requestURI}`);

            // Handle expired/invalid auth (401). This helper never serves
            // auth/* endpoints, so an unconditional check is safe.
            if (response.status === 401) {
                handleAuthFailure();
                throw new Error('Unauthorized');
            }

            throw new Error(`FetchBinary failed (${response.status})`);
        }

        // Successful response: clear the auth-failure latch.
        isHandlingAuthFailure = false;

        return response.arrayBuffer();
    } catch (e) {
        console.error("FetchBinary: Unexpected error occurred.", e);
        throw e;
    }
};

/**
 * Parse an array of objects into a Map-like object indexed by 'id'.
 */
export const ParseData = (data) => {
    if (!data || !Array.isArray(data)) {
        return {};
    }
    return data.reduce((acc, item) => {
        if (item && item.id !== undefined) {
            acc[item.id] = item;
        }
        return acc;
    }, {});
};

/**
 * Load a specific resource and store it in localStorage.
 * Fetches first 100 items only (for initial cache used by other features).
 */
export const LoadResource = async (resource) => {
    // Capture the storage backend (localStorage vs. sessionStorage) at the moment the
    // request is ISSUED, not when it resolves. `Get()` below can take a while, and
    // LoadResourcesAll() fires ~20 of these concurrently with no cancellation. If the
    // session context changes mid-flight (e.g. a delegate session is cleared by a 401 on
    // one of the sibling requests), re-resolving the backend fresh at write time would
    // redirect this write to the wrong storage. Even in the worst case — the delegate
    // session is cleared while this request is still in flight — a write captured
    // against the original sessionStorage reference stays confined to sessionStorage
    // (tab-scoped, and about to be cleared/gone with the tab anyway); it can never leak
    // into the host tab's shared localStorage. This is strictly safer than re-resolving
    // at write time. See VOIP-1316 follow-up.
    const storage = getResourceStorage();
    try {
        const target = `${resource}?page_size=100`;
        const result = await Get(target);

        // Extract data based on different response formats
        const data = result && result.result ? result.result : (Array.isArray(result) ? result : []);

        if (!Array.isArray(data)) {
            console.warn(`LoadResource: Unexpected format for ${resource}. Expected array.`);
            writeResourceCacheTo(storage, resource, JSON.stringify({}));
            return;
        }

        const parsedData = ParseData(data);
        writeResourceCacheTo(storage, resource, JSON.stringify(parsedData));
        console.log(`LoadResource: Cached ${resource}. Items: ${Object.keys(parsedData).length}`);
    } catch (e) {
        console.error(`LoadResource: Error loading resource '${resource}':`, e);
    }
};

/**
 * Load all relevant resources based on Agent permissions.
 */
export const LoadResourcesAll = async () => {
    console.log("LoadResourcesAll: Initializing global resource load...");
    const agentInfo = getAgentInfoFromStore();
    
    if (!agentInfo) {
        console.warn("LoadResourcesAll: Agent information missing. Aborting load.");
        return;
    }

    const resources = new Set();

    // Determine resources to load based on RBAC
    if (AgentHasPermissionProjectAdmin(agentInfo)) {
        ["customers", "providers", "routes", "billing_accounts", "billings", "numbers",
         "agents", "campaigns", "ais", "conferences", "conferencecalls", "extensions",
         "flows", "outdials", "queues", "recordings", "tags", "transcribes", "trunks",
         "webchat_widgets"
        ].forEach(r => resources.add(r));
    } else if (AgentHasPermissionCustomerAdmin(agentInfo)) {
        ["billing_accounts", "billings", "numbers", "agents", "campaigns", "ais",
         "conferences", "conferencecalls", "extensions", "flows", "outdials", "queues",
         "recordings", "tags", "transcribes", "trunks", "webchat_widgets"
        ].forEach(r => resources.add(r));
    } else if (AgentHasPermissionCustomerManager(agentInfo)) {
        ["agents", "campaigns", "ais", "conferences", "conferencecalls", "extensions",
         "flows", "outdials", "queues", "recordings", "tags", "transcribes", "trunks",
         "webchat_widgets"
        ].forEach(r => resources.add(r));
    }

    // Default common resources
    ["calls", "conversations", "messages"].forEach(resource => resources.add(resource));

    if (resources.size === 0) return;

    console.log(`LoadResourcesAll: Syncing ${resources.size} resources...`);

    const loadPromises = Array.from(resources).map(resource => LoadResource(resource));
    try {
        await Promise.all(loadPromises);
        console.log("LoadResourcesAll: All resources synchronized successfully.");
    } catch (e) {
        console.error("LoadResourcesAll: Failed to complete full resource sync.", e);
    }
};